import React from 'react';
import { Box, Card, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { upperFirst } from 'lodash';

import EventDates from './partials/EventDates';
import { moment } from '../../../I18n';
import { stripHtml } from '../../../Utils';
import { isFolder } from '../../../Utils/Post';
import CardActionArea from './partials/CardActionArea';
import CardContent from './partials/CardContent';
import CardMedia from './partials/CardMedia';
import Location from './partials/Location';
import EventUrl from './partials/EventUrl';
import PostIndicators from '../../Posts/Shared/PostIndicators';
import ConsultationProgress from './partials/ConsultationProgress';
import { Images } from '../../../Themes';
import AnalyticsService from '../../../Services/AnalyticsService';
import mapPropsToType from './mapPropsToType';
import { postPath } from '../../../Utils/routes';
import { cloudinaryImageUrl } from '../../../Services/Cloudinary';
import { Post, Source, PostType } from '../../../Utils/Types/Posts';
import { Space } from '../../../Utils/Types/Space';

interface PostCardProps {
  trackingProps?: {
    category: string;
    name: string;
  };
  entity: Space;
  parent?: Post | Source;
  post: Post;
  newTab?: boolean;
  direction?: 'row' | 'column';
}

function PostCard({
  trackingProps,
  post,
  parent,
  entity,
  newTab,
  direction = 'column',
}: PostCardProps) {
  const postTypeDefaultProps = mapPropsToType[post.type];
  const eventDetail = !!postTypeDefaultProps.eventDetail;
  const consultationProgress = !!postTypeDefaultProps.consultationProgress;
  const publishingDetail = !!postTypeDefaultProps.publishingDetail;

  const onClick = () => {
    if (trackingProps?.category && trackingProps?.name) {
      AnalyticsService.track(trackingProps.category, trackingProps.name, '', {
        'Post ID': post.id,
        'Post Type': post.type,
        'Post Pinned': !!post.is_pinned,
        'Parent ID': parent?.id,
      });
    }
  };

  const postImage =
    cloudinaryImageUrl(
      post.picture?.s3_key,
      'w_295,h_200,q_auto:low,c_fill,g_auto',
      post.picture?.title,
    ) || Images.posts.banner[post.type as PostType];

  return (
    <Card sx={{ width: '100%', height: direction === 'row' ? '100%' : 'auto' }}>
      <CardActionArea
        to={postPath(entity.slug, post.slug)}
        sx={{ height: '100%' }}
        component={Link}
        onClick={onClick}
        underline="none"
        target={newTab ? '_blank' : '_self'}
        direction={direction}
      >
        <CardMedia image={postImage} direction={direction} />

        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" component="h2" className="line-clamp">
              {post.title}
            </Typography>
          </Box>

          {post.problematic && isFolder(post.type) && (
            <Box className="line-clamp" sx={{ mb: 1 }}>
              <Typography variant="body1">{stripHtml(post.problematic)}</Typography>
            </Box>
          )}

          {eventDetail && (
            <>
              <EventDates post={post} />
              <Location post={post} />
              <EventUrl post={post} />
            </>
          )}

          {consultationProgress && (
            <Box>
              <ConsultationProgress post={post} />
            </Box>
          )}

          {direction !== 'row' && post.post_contributions_stats && (
            <Box sx={{ marginTop: 'auto', paddingTop: 2 }}>
              <PostIndicators postContributionsStats={post.post_contributions_stats} />
            </Box>
          )}

          {publishingDetail && (
            <>
              <Box className="line-clamp" sx={{ mb: 2 }}>
                <Typography variant="body1">{stripHtml(post.content)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'medium' }}>
                  {upperFirst(moment(post.published_at).fromNow())}
                </Typography>
              </Box>
            </>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default PostCard;
